import { useState } from 'react';
import { IBrand, IClient } from '../types/brand';
import {
  MoreVertical,
  Pencil,
  Plus,
  UserPlus,
  X,
  Minus,
  ExternalLink,
} from 'lucide-react';
import { getClientBrandAgents, getClientBrands, safeLink } from '../utils';
import OnboardAgent from './forms/OnboardAgent';
import OffboardAgent from './forms/OffboardAgent';
import { IAgent } from '../types/agent';
import Modal from './common/Modal';
import { useNavigate } from 'react-router-dom';

const OptionsModal = ({
  onAddAgent,
  onOffboardAgent,
  onBrandEdit,
  brand,
  handleOptionClose,
}: {
  onAddAgent: (brand: IBrand) => void;
  onOffboardAgent: (brand: IBrand) => void;
  onBrandEdit: (brand: IBrand) => void;
  brand: IBrand;
  handleOptionClose: () => void;
}) => {
  return (
    <div className="flex flex-col gap-2 h-fit bg-white shadow-lg rounded-lg border border-greyThirty p-2 min-w-36 ">
      <button
        onClick={() => {
          onBrandEdit(brand);
          handleOptionClose();
        }}
        className="text-sm text-blackTwenty hover:bg-greyThirty rounded-md p-1 flex items-center gap-2"
      >
        <Pencil size={14} /> Edit Brand
      </button>
      <button
        onClick={() => {
          onAddAgent(brand);
          handleOptionClose();
        }}
        className="text-sm text-blackTwenty hover:bg-greyThirty rounded-md p-1 flex items-center gap-2 w-full whitespace-nowrap"
      >
        <UserPlus size={14} /> Onboard Agent
      </button>
      <button
        onClick={() => {
          onOffboardAgent(brand);
          handleOptionClose();
        }}
        className="text-sm text-blackTwenty hover:bg-greyThirty rounded-md p-1 flex items-center gap-2 w-full whitespace-nowrap"
      >
        <Minus size={14} /> Offboard Agent
      </button>
    </div>
  );
};

const ClientInfo = ({
  agentsData,
  rowData,
  client,
  onClose,
  handleBrandEdit,
}: {
  agentsData: IAgent[];
  rowData: any;
  client: IClient;
  onClose: () => void;
  handleBrandEdit: (brand: IBrand) => void;
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOffboardModalOpen, setIsOffboardModalOpen] = useState(false);
  const [optionsBrandId, setOptionsBrandId] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<IBrand | null>(null);
  const [expandedBrand, setExpandedBrand] = useState<IBrand | null>(null);
  const brands = getClientBrands(rowData, client?._id!);

  const handleAddAgent = (brand: IBrand) => {
    setIsModalOpen(!isModalOpen);
    setSelectedBrand(brand);
  };

  const handleOffboardAgent = (brand: IBrand) => {
    setIsOffboardModalOpen(!isOffboardModalOpen);
    setSelectedBrand(brand);
  };

  const clientBrandAgents = getClientBrandAgents(brands, agentsData);

  const getAgentForBrand = (brand: IBrand) => {
    return clientBrandAgents.filter((agent) =>
      brand.agents?.includes(agent.kimId!)
    );
  };

  const handleOptionToggle = (brandId: string) => {
    setOptionsBrandId(optionsBrandId === brandId ? null : brandId);
  };

  const handleOptionClose = () => {
    setOptionsBrandId(null);
  };

  const handleExpand = (brand: IBrand) => {
    if (expandedBrand?._id === brand._id) {
      setExpandedBrand(null);
    } else {
      setExpandedBrand(brand);
    }
  };

  return (
    <div className="py-3 px-5 space-y-6 h-full overflow-y-auto">
      <div className="relative top-0 -left-2">
        <button
          onClick={onClose}
          className="text-sm text-gray-600 hover:text-black"
        >
          <X />
        </button>
      </div>
      <div className="flex justify-between items-center bg-white p-5 rounded-xl shadow-sm border border-greyThirty">
        <div className="flex flex-col">
          <h1 className="text-xl font-semibold text-blackFourty">
            {client?.name}
          </h1>
          <p className="text-sm text-greyEighty">{client?.email}</p>
        </div>
        <div className="flex items-center gap-5">
          <div className="flex flex-col items-center">
            <h2 className="text-md font-semibold text-blackFourty">Brands</h2>
            <p className="text-sm text-greyEighty">{brands?.length}</p>
          </div>
          <div className="flex flex-col items-center">
            <h2 className="text-md font-semibold text-blackFourty">Agents</h2>
            <p className="text-sm text-greyEighty">
              {clientBrandAgents?.length}
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-[40px_1fr_1fr_1fr_1fr_80px] gap-4 bg-white p-3 font-medium rounded-xl shadow-sm border border-greyThirty">
        <p className="text-sm text-blackTwenty col-span-[20px]"></p>
        <p className="text-sm text-blackTwenty">Brand Details</p>
        <p className="text-sm text-blackTwenty">Alias</p>
        <p className="text-sm text-blackTwenty">Website URL</p>
        <p className="text-sm text-blackTwenty text-center">No of Agents</p>
        <p className="text-sm text-blackTwenty text-center">Action</p>
      </div>
      <div className="flex flex-col space-y-3">
        {brands &&
          brands?.map((brand: IBrand) => {
            const agents = getAgentForBrand(brand);
            const isExpanded = expandedBrand?._id === brand._id;

            return (
              <div key={brand._id} className="space-y-2">
                {/* Brand Row */}
                <div className="relative grid grid-cols-[40px_1fr_1fr_1fr_1fr_80px] gap-4 items-center bg-white p-4 font-medium rounded-xl shadow-sm border border-greyThirty">
                  {/* Expand/Collapse */}
                  <button
                    onClick={() => handleExpand(brand)}
                    className="flex items-center justify-center hover:bg-greyTwenty p-1 rounded-md text-blackTwenty"
                  >
                    {isExpanded ? <Minus size={16} /> : <Plus size={16} />}
                  </button>

                  {/* Brand Info */}
                  <p
                    className="text-md text-blackTwenty truncate"
                    title={brand.name}
                  >
                    {brand.name}
                  </p>
                  <p
                    className="text-sm text-blackTwenty truncate"
                    title={brand.alias || '--'}
                  >
                    {brand.alias || '--'}
                  </p>
                  <a
                    href={safeLink(brand.websiteLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blackTwenty truncate hover:underline"
                  >
                    Visit site <ExternalLink size={16} />
                  </a>
                  <p className="text-sm text-blackTwenty text-center">
                    {agents?.length || 0}
                  </p>

                  {/* Options */}
                  <div className="flex justify-center relative">
                    <button
                      onClick={() => handleOptionToggle(brand._id!)}
                      className="text-blackTwenty hover:bg-greyThirty rounded-full p-1"
                    >
                      <MoreVertical size={14} />
                    </button>
                    {optionsBrandId === brand._id && (
                      <div className="absolute top-10 right-0 z-20">
                        <OptionsModal
                          onAddAgent={handleAddAgent}
                          onOffboardAgent={handleOffboardAgent}
                          onBrandEdit={handleBrandEdit}
                          brand={brand}
                          handleOptionClose={handleOptionClose}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Agents Tree Branch */}
                {isExpanded && (
                  <div className="ml-6 pl-4 border-l-2 border-greyThirty space-y-2">
                    {agents.length > 0 ? (
                      agents.map((agent) => (
                        <div
                          onClick={() => navigate(`/agents/${agent._id}`)}
                          key={agent.kimId}
                          className="relative bg-white px-3 py-1.5 rounded-lg shadow-sm border border-greyTwenty flex justify-between items-center"
                        >
                          <div>
                            <p className="font-medium text-blackThirty">
                              {agent.name}
                            </p>
                            <p className="text-xs text-greyEighty">
                              {agent.personalEmail}
                            </p>
                          </div>
                          <button className="bg-greyTen hover:bg-greyTwenty hover:border-greyThirty border border-greyTwenty rounded-md px-2 py-1 text-xs">
                            View
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm italic text-greyEighty ml-2">
                        No agents onboarded yet.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          className="!w-[40%] 3xl:!w-[30%]"
          minHeight="70%"
          title="Onboard Agent"
        >
          <OnboardAgent
            initialAgents={agentsData}
            selectedBrand={selectedBrand}
            onClose={() => setIsModalOpen(false)}
            onSuccess={() => setIsModalOpen(false)}
          />
        </Modal>
      )}
      {isOffboardModalOpen && selectedBrand && (
        <Modal
          isOpen={isOffboardModalOpen}
          onRequestClose={() => setIsOffboardModalOpen(false)}
          className="!w-[40%] 3xl:!w-[30%]"
          minHeight="50%"
          title="Offboard Agent"
        >
          <OffboardAgent
            selectedBrand={selectedBrand}
            brandAgents={getAgentForBrand(selectedBrand)}
            onClose={() => setIsOffboardModalOpen(false)}
            onSuccess={() => setIsOffboardModalOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default ClientInfo;
